import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { patientApi } from '../../api/patientApi';

// Async thunks
export const fetchDashboardStats = createAsyncThunk(
  'patientDashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getDashboardStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchAppointmentHistory = createAsyncThunk(
  'patientDashboard/fetchAppointmentHistory',
  async (months = 6, { rejectWithValue }) => {
    try {
      const response = await patientApi.getAppointmentHistory(months);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch appointment history');
    }
  }
);

export const fetchHealthSummary = createAsyncThunk(
  'patientDashboard/fetchHealthSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getHealthSummary();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch health summary');
    }
  }
);

// Additional thunks for the new endpoints
export const fetchRecentAppointments = createAsyncThunk(
  'patientDashboard/fetchRecentAppointments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getRecentAppointments();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch recent appointments');
    }
  }
);

export const fetchUpcomingAppointments = createAsyncThunk(
  'patientDashboard/fetchUpcomingAppointments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getUpcomingAppointments();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch upcoming appointments');
    }
  }
);

export const fetchPrescriptions = createAsyncThunk(
  'patientDashboard/fetchPrescriptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getPrescriptions();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch prescriptions');
    }
  }
);

export const fetchHealthMetrics = createAsyncThunk(
  'patientDashboard/fetchHealthMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getHealthMetrics();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch health metrics');
    }
  }
);

const initialState = {
  stats: {
    data: null,
    loading: false,
    error: null
  },
  appointmentHistory: {
    data: null,
    loading: false,
    error: null
  },
  healthSummary: {
    data: null,
    loading: false,
    error: null
  },
  recentAppointments: {
    data: null,
    loading: false,
    error: null
  },
  upcomingAppointments: {
    data: null,
    loading: false,
    error: null
  },
  prescriptions: {
    data: null,
    loading: false,
    error: null
  },
  healthMetrics: {
    data: null,
    loading: false,
    error: null
  }
};

const patientDashboardSlice = createSlice({
  name: 'patientDashboard',
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.stats = initialState.stats;
      state.appointmentHistory = initialState.appointmentHistory;
      state.healthSummary = initialState.healthSummary;
      state.recentAppointments = initialState.recentAppointments;
      state.upcomingAppointments = initialState.upcomingAppointments;
      state.prescriptions = initialState.prescriptions;
      state.healthMetrics = initialState.healthMetrics;
    }
  },
  extraReducers: (builder) => {
    // Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        state.stats.data = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload;
      })
      // Appointment History
      .addCase(fetchAppointmentHistory.pending, (state) => {
        state.appointmentHistory.loading = true;
        state.appointmentHistory.error = null;
      })
      .addCase(fetchAppointmentHistory.fulfilled, (state, action) => {
        state.appointmentHistory.loading = false;
        state.appointmentHistory.data = action.payload;
      })
      .addCase(fetchAppointmentHistory.rejected, (state, action) => {
        state.appointmentHistory.loading = false;
        state.appointmentHistory.error = action.payload;
      })
      // Health Summary
      .addCase(fetchHealthSummary.pending, (state) => {
        state.healthSummary.loading = true;
        state.healthSummary.error = null;
      })
      .addCase(fetchHealthSummary.fulfilled, (state, action) => {
        state.healthSummary.loading = false;
        state.healthSummary.data = action.payload;
      })
      .addCase(fetchHealthSummary.rejected, (state, action) => {
        state.healthSummary.loading = false;
        state.healthSummary.error = action.payload;
      })
      // Recent Appointments
      .addCase(fetchRecentAppointments.pending, (state) => {
        state.recentAppointments.loading = true;
        state.recentAppointments.error = null;
      })
      .addCase(fetchRecentAppointments.fulfilled, (state, action) => {
        state.recentAppointments.loading = false;
        state.recentAppointments.data = action.payload;
      })
      .addCase(fetchRecentAppointments.rejected, (state, action) => {
        state.recentAppointments.loading = false;
        state.recentAppointments.error = action.payload;
      })
      // Upcoming Appointments
      .addCase(fetchUpcomingAppointments.pending, (state) => {
        state.upcomingAppointments.loading = true;
        state.upcomingAppointments.error = null;
      })
      .addCase(fetchUpcomingAppointments.fulfilled, (state, action) => {
        state.upcomingAppointments.loading = false;
        state.upcomingAppointments.data = action.payload;
      })
      .addCase(fetchUpcomingAppointments.rejected, (state, action) => {
        state.upcomingAppointments.loading = false;
        state.upcomingAppointments.error = action.payload;
      })
      // Prescriptions
      .addCase(fetchPrescriptions.pending, (state) => {
        state.prescriptions.loading = true;
        state.prescriptions.error = null;
      })
      .addCase(fetchPrescriptions.fulfilled, (state, action) => {
        state.prescriptions.loading = false;
        state.prescriptions.data = action.payload;
      })
      .addCase(fetchPrescriptions.rejected, (state, action) => {
        state.prescriptions.loading = false;
        state.prescriptions.error = action.payload;
      })
      // Health Metrics
      .addCase(fetchHealthMetrics.pending, (state) => {
        state.healthMetrics.loading = true;
        state.healthMetrics.error = null;
      })
      .addCase(fetchHealthMetrics.fulfilled, (state, action) => {
        state.healthMetrics.loading = false;
        state.healthMetrics.data = action.payload;
      })
      .addCase(fetchHealthMetrics.rejected, (state, action) => {
        state.healthMetrics.loading = false;
        state.healthMetrics.error = action.payload;
      });
  }
});

export const { clearDashboardData } = patientDashboardSlice.actions;
export default patientDashboardSlice.reducer; 